
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import { db, functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import _isNil from 'lodash/isNil';
import _trim from 'lodash/trim';
import _keys from 'lodash/keys';
import _isString from 'lodash/isString';
import _isObject from 'lodash/isObject';
import _isNumber from 'lodash/isNumber';
import { MyBalanceMixin, RunnitsCRUDMixin, TeamUserMixin } from '@/mixins';
import {
	getRunnitNodeRunPrice,
	RunnitNodeStaticFields,
	RunnitNodeStaticFieldsKey,
	RUNNIT_NODE_DEF_PRICING_TYPE,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	FieldDefUuid,
	RunnitNodeRunInputValue,
	RUNNIT_NODE_FIELD_TYPE,
	RunnitNodeFieldDisplayIMG,
	RunnitNodeFieldDisplayIMGS,
	RunnitNodeFieldDisplayVIDEO,
	RunnitNodeFieldDisplayPROMPT,
	isFieldRequired,
	extractInputMediaDimensions,
	eachVisibleLeafField,
	eachUnsatisfiedRequiredGroup,
	getLogicOnFieldsFromField,
	tokensToHundredths,
	hundredthsToTokens,
	formatCooldownTimer,
	getCooldownTimeBucketShort,
	getCooldownChipLabel,
	RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER,
	RunnitNodeFieldDisplayWIDTH_HEIGHT,
	RunnitNodeFieldDisplaySINGLE_SELECT,
	RunnitNodeFieldDisplaySLIDER,
	RunnitNodeFieldDisplayCFG,
	RunnitNodeFieldDisplaySTEPS,
} from '@run-diffusion/shared';
import { validateWidthHeightValue } from '@/views/Runnits/RunnitSettings/WidthHeightField/utils';
import { validateSingleSelectValue } from '@/views/Runnits/RunnitSettings/SingleSelect/utils';
import { validateSliderValue } from '@/views/Runnits/RunnitSettings/Sliders/utils';
import TokensSVG from '@/assets/TokensSVG.vue';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import { RUNNITS_OWNER_SELECTION } from './constants';
import BaseButton from '@/components/base/BaseButton.vue';
import CooldownLabelButton from '@/components/CooldownLabelButton.vue';
import { ModelCRUDMixin } from "@/mixins/ModelCRUDMixin";
import RunnitModelTrainingDialog from "@/views/Runnits/base/RunnitModelTrainingDialog.vue";
import BigNumber from 'bignumber.js';
import { DEFAULT_TEXT_MAX_LENGTH, getProxyAdminToken, trackGtmRunGeneration } from '@/utils';
import { analytics, EVENTS } from '@/services/analytics';
import moment from 'moment-timezone';
import pluralize from 'pluralize';
import { getDomainClaimRestrictionMessage } from '@/utils/domainClaimRestriction';

export default Vue.extend({
	name: 'RunRunnitButton',
	mixins: [
		MyBalanceMixin,
		RunnitBulkActionsMixin,
		ModelCRUDMixin,
		RunnitsCRUDMixin,
		TeamUserMixin,
	],
	props: {
		selectedNode: { type: Object, default: null },
		actionsOnly: { type: Boolean, default: false },
		actionText: { type: String, default: 'Run' },
		validate: { type: Function, default: null },
		formValid: { type: Boolean, default: true },
		primaryFormMainAction: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		disabledReason: { type: String, default: null },
	},
	data () {
		return {
			RUNNIT_NODE_STATIC_FIELDS_KEY,
			RUNNIT_NODE_DEF_TOOL_APP_TYPE,
			isModelTrainingDialogOpen: false,
			fetchingTokenCooldownStatus: false,
			lastFetchedCooldownTeamId: null as string | null,
		};
	},
	created () {
		// Add event listeners for keydown when the component is created
		document.addEventListener('keydown', this.handleKeyDown);
	},
	mounted () {
		this.maybeFetchTokenCooldownStatus();
	},
	destroyed () {
		// Remove event listeners for keydown when the component is destroyed
		document.removeEventListener('keydown', this.handleKeyDown);
	},
	watch: {
		runnitButtonDisabled: {
			immediate: true,
			handler (newVal: { disabled: boolean, reasons: string[] }, oldVal: { disabled: boolean, reasons: string[] }) {
				if ((newVal?.disabled !== oldVal?.disabled) || (JSON.stringify(newVal?.reasons) !== JSON.stringify(oldVal?.reasons))) {
					this.$emit('runnit-button-disabled-changed', newVal);
				}
			},
		},
		isCooldownActive: {
			handler (isActive: boolean) {
				// Clear wasBlockedByCooldown when cooldown expires
				if (!isActive && this.runnitState.tokenCooldownData?.wasBlockedByCooldown) {
					this.updateTokenCooldownData({
						wasBlockedByCooldown: false,
					});
				}
			},
		},
		// Fetch cooldown data when on team and we don't have it yet (so cooldown checks can run)
		'runnitState.runnitsOwnerSelection': {
			handler () {
				this.maybeFetchTokenCooldownStatus();
			},
		},
		'team.id': {
			handler () {
				this.maybeFetchTokenCooldownStatus();
			},
		},
	},
	computed: {
		...mapState([
			'user',
			'team',
			'teamBalanceAccount',
			'loadingDraftRunnitNodeRun',
			'draftRunnitNodeRun',
			'runnitState',
			'modelsState',
			'realTimeMoment',
		]),
		...mapGetters([
			'runnitNodesLimitMap',
			'displayLabels',
			'activeDomainClaimRestriction',
			'companyClubOfferBenefits',
		]),
		inputValues () {
			return this.runnitState.inputValues
		},
		staticInputValues () {
			return this.runnitState.staticInputValues
		},
		isToolUnpublished () {
			return !!(this.selectedNode &&
				typeof this.selectedNode.nodeDef !== 'string' && // Nested object is fetched
				(!this.selectedNode.nodeDef.isPublished || !this.selectedNode.nodeDef.publishedAt) &&
				!this.user.isAdmin);
		},
		isToolBlockedForTeamUser () {
			if (!this.selectedNode || typeof this.selectedNode.nodeDef === 'string') return false;
			return this.isNodeDefBlockedForTeamUser(this.selectedNode.nodeDef);
		},
		// A deactivated team keeps its past generations but can't run new ones (runRunnitNode
		// refuses them too). The button says so instead of Run.
		isTeamDeactivated (): boolean {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.team &&
				!this.isTeamActive
			);
		},
		teamDeactivatedLabel (): string {
			return `${this.displayLabels.team} deactivated`;
		},
		teamDeactivatedMessage (): string {
			return `This ${this.displayLabels.team} is deactivated, so it can't run new generations. Past generations stay available to view and download.`;
		},
		runnitButtonDisabled () {
			// A deactivated team can't run anything, whatever else holds
			if (this.isTeamDeactivated) {
				return {
					disabled: true,
					reasons: [this.teamDeactivatedLabel],
				};
			}

			if (this.disabled) {
				return {
					disabled: true,
					reasons: [this.disabledReason || 'Disabled'],
				};
			}

			if (!this.hasGenerateRunnitPermission) {
				return {
					disabled: true,
					reasons: ['Unauthorized'],
				};
			}
			if (this.activeDomainClaimRestriction) {
				return {
					disabled: true,
					reasons: ['Domain restricted'],
				};
			}

			// Token limit does not apply to trainer (model training) runs
			if (this.hasTokenLimitPermission && this.selectedNode?.nodeDef?.appType !== RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
				// The cap for THIS tool: the role's per-media-type override when the
				// tool carries that MEDIA tag, else the role's master cap. Same
				// resolution the server enforces, so the button and the run agree.
				// Same `typeof === 'string'` guard the rest of this file uses: an
				// unhydrated nodeDef ref carries no tags, and passing the ref string
				// would silently resolve to the master cap instead of the tool's.
				const selectedNodeDef = (this.selectedNode && typeof this.selectedNode.nodeDef !== 'string')
					? this.selectedNode.nodeDef
					: null;
				const tokenLimitMax = this.getTokenLimitMaxForNodeDef(this.team, this.teamUserRole, selectedNodeDef);
				if (!tokenLimitMax) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Your generation token limit is not set. Please contact your ${this.displayLabels.team} admin to set it. You will be unable to generate anything until this is resolved.`,
						show: true,
					});
					return {
						disabled: true,
						reasons: [`token limit not set`],
					};
				}
				// Check either total run cost or per-generation cost based on applyToTotalRun setting
				const costToCheck = this.tokenLimitApplyToTotalRun ? this.totalRunCost : this.finalPricePerResult;
				if (costToCheck !== null && costToCheck > tokenLimitMax) {
					return {
						disabled: true,
						reasons: [this.tokenLimitApplyToTotalRun ? `exceeds total run token limit` : `exceeds generation token limit`],
					};
				}
			}

			let disabled = false;
			let reasons = [];

			if (!this.selectedNode) {
				disabled = true;
				reasons.push('No tool selected');
			}

			if (
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				!this.team
			) {
				disabled = true;
				reasons.push(`No ${this.displayLabels.team}`);
			}

			if (!this.runnitNodesLimitMap[this.selectedNode.id]) {
				disabled = true;
				reasons.push('Tool limit reached');
			}

			if (_isNil(this.totalRunCost)) {
				disabled = true;
				reasons.push('No cost');
			}

			const canGoNegative: boolean = !!(
				this.teamBalanceAccount?.type === 'TEAM' &&
				this.companyClubOfferBenefits.isEnterprise &&
				!this.team.allotmentSettings?.isBoundToBudget
			);
			if (!canGoNegative && this.totalRunCost > this.balanceAccountRunnitTokensData.combinedTokens) {
				disabled = true;
				reasons.push('Insufficient tokens');
			}

			if (this.runnitState.isQueuingDraftRunnitNodeRun) {
				disabled = true;
				reasons.push('Queuing');
			}

			// Cooldown budget: disable only if we're still within the cooldown window and run cost exceeds tokens remaining
			// Past endAt/resetAt => treat as full budget (don't disable, chip goes away)
			const cooldownDataForBudget = this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				? this.runnitState.tokenCooldownData?.cooldownData
				: null;
			if (
				(cooldownDataForBudget &&
					(
						typeof cooldownDataForBudget.tokensRemaining === 'number' &&
						!_isNil(this.totalRunCost) &&
						this.isCooldownActive &&
						this.totalRunCost > cooldownDataForBudget.tokensRemaining
					)) ||
				(this.cooldownData && !_isNil(this.totalRunCost) && this.totalRunCost > this.cooldownData.threshold)
			) {
				disabled = true;
				reasons.push('cooldown budget exceeded');
			}

			if (this.disableActions) {
				disabled = true;
				reasons.push('actions disabled');
			}

			if (this.actionsOnly && !this.formValid) {
				disabled = true;
				reasons.push('Form invalid');
			}

			if (this.isToolUnpublished) {
				disabled = true;
				reasons.push('Unpublished');
			}

			if (this.isToolBlockedForTeamUser) {
				disabled = true;
				reasons.push('You do not have permission to access this tool');
			}

			return {
				disabled,
				reasons,
			};
		},
		disableActions () {
			return this.loadingDraftRunnitNodeRun || this.loadingDraftRunnitNodeRun === null;
		},
		determinedStaticFields () {
			if (!this.selectedNode) return {};
			return this._get(this.selectedNode, 'nodeDef.staticFields') || {};
		},
		numResultsEffectiveMax (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMax: number = numResultsField?.max ?? 1;
			if (!numResultsField) return 1;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(toolMax, this.numResultsLimitMax);
			}
			return toolMax;
		},
		showNumResultsInput (): boolean {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			if (!numResultsField) return false;
			const toolMin: number = numResultsField.min ?? 1;
			return toolMin < this.numResultsEffectiveMax;
		},
		numResultsStaticInputValue (): number {
			const numResultsField: RunnitNodeStaticFields['numResults'] = this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const toolMin: number = numResultsField?.min ?? 1;
			const fromStaticInput: number = this.staticInputValues[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults];
			const val: number = fromStaticInput > 0 ? fromStaticInput : toolMin;
			if (!this.showNumResultsInput) return toolMin;
			if (this.hasNumResultsLimitPermission && this.numResultsLimitMax) {
				return Math.min(this.numResultsEffectiveMax, Math.max(toolMin, val));
			}
			return Math.max(toolMin, val);
		},
		totalRunCost () {
			if (
				this.selectedNode &&
				this.selectedNode.nodeDef &&
				typeof this.selectedNode.nodeDef !== 'string' && // Nested object is fetched
				this.selectedNode.nodeDef.pricingType === RUNNIT_NODE_DEF_PRICING_TYPE.COST &&
				this.selectedNode.nodeDef.costPerResult > 0
			) {
				const determinedFields = this.selectedNode.nodeDef.fields || [];
				return getRunnitNodeRunPrice(this.selectedNode.nodeDef.costPerResult, this.numResultsStaticInputValue, this.selectedNode.nodeDef.costPerResultLogics, this.inputValues, determinedFields);
			}
			return null; // null means can't get price yet, should disable the run button
		},
		canModifierEnterGenerate () {
			return !!(
				!_isNil(this.totalRunCost) &&
				!this.runnitButtonDisabled.disabled &&
				this.totalRunCost < tokensToHundredths(1000)
			);
		},
		cooldownCountdownData () {
			// Only show countdown if user was blocked by cooldown AND cooldown is still active
			const cooldownState = this.runnitState.tokenCooldownData;
			if (!cooldownState || !cooldownState.wasBlockedByCooldown || !this.isCooldownActive) {
				return null;
			}
			const resetAtMoment = moment(cooldownState.cooldownEndsAt);
			const seconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			const secs = seconds % 60;
			return {
				hours,
				minutes,
				seconds: secs,
				totalSeconds: seconds,
			};
		},
		cooldownCountdownText () {
			if (!this.cooldownCountdownData || this.cooldownCountdownData.totalSeconds <= 0) {
				return '';
			}
			if (!this.cooldownEndsAt || !this.realTimeMoment) return '';
			return moment(this.cooldownEndsAt).from(this.realTimeMoment, true);
		},
		/** True when disabled because run cost exceeds cooldown budget (used for chip + open menu on click). */
		disabledDueToCooldownBudget (): boolean {
			return !!this.runnitButtonDisabled.reasons?.includes('cooldown budget exceeded');
		},
		/** Timer for chip: "4:13 / 5m" or "1:27:00 / 2h" (shared util). */
		cooldownChipTimerText (): string {
			if (!this.cooldownEndsAt || !this.realTimeMoment) return '';
			const resetAtMoment = moment(this.cooldownEndsAt);
			const totalSeconds = Math.max(0, resetAtMoment.diff(this.realTimeMoment, 'seconds'));
			const bucketShort = getCooldownTimeBucketShort(this.cooldownData?.timeBucket);
			return formatCooldownTimer(totalSeconds, bucketShort);
		},
		cooldownTimeBucketShort (): string {
			return getCooldownTimeBucketShort(this.cooldownData?.timeBucket);
		},
		/** Chip label when disabled due to cooldown (shared util). */
		cooldownChipLabel (): string {
			if (!this.disabledDueToCooldownBudget || !this.cooldownData) return '';
			return getCooldownChipLabel(this.totalRunCost, this.cooldownData.threshold, this.cooldownChipTimerText);
		},
		isCooldownActive () {
			const cooldownState = this.runnitState.tokenCooldownData;
			if (!cooldownState || !cooldownState.cooldownEndsAt || !this.realTimeMoment) {
				return false;
			}
			const resetAtMoment = moment(cooldownState.cooldownEndsAt);
			return resetAtMoment.isAfter(this.realTimeMoment);
		},
		cooldownData () {
			return this.runnitState.tokenCooldownData?.cooldownData ?? null;
		},
		cooldownEndsAt () {
			return this.runnitState.tokenCooldownData?.cooldownEndsAt ?? null;
		},
		cooldownTokensRemainingCount () {
			if (!this.cooldownData) {
				return 0;
			}
			return hundredthsToTokens(this.cooldownData.tokensRemaining);
		},
		finalPricePerResult () {
			if (
				this.selectedNode?.nodeDef?.pricingType === RUNNIT_NODE_DEF_PRICING_TYPE.COST &&
				this.selectedNode?.nodeDef?.costPerResult > 0
			) {
				const determinedFields = this.selectedNode.nodeDef.fields || [];
				// Calculate price for a single result (numResults = 1)
				return getRunnitNodeRunPrice(this.selectedNode.nodeDef.costPerResult, 1, this.selectedNode.nodeDef.costPerResultLogics, this.inputValues, determinedFields);
			}
			return null; // null means can't get price yet
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateTokenCooldownData',
		]),
		_isNil,
		setSettingsDrawerOpen (value: boolean) {
			this.$emit('set-settings-drawer-open', value);
			if (value) {
				this.setBulkActionMenuOpen(false);
			}
		},
		/** Call when we might need cooldown data: on team, once per team (or when we have no data yet). */
		maybeFetchTokenCooldownStatus () {
			if (this.runnitState.runnitsOwnerSelection !== RUNNITS_OWNER_SELECTION.TEAM || !this.team?.id) {
				this.lastFetchedCooldownTeamId = null;
				return;
			}
			// Fetch once per team so we get cooldown data for disable checks (avoid refetch when backend returns no cooldown)
			if (this.lastFetchedCooldownTeamId === this.team.id || this.fetchingTokenCooldownStatus) {
				return;
			}
			this.fetchTokenCooldownStatus();
		},
		async fetchTokenCooldownStatus () {
			if (!this.team?.id) return;
			this.fetchingTokenCooldownStatus = true;
			try {
				const getTokenCooldownStatusFn = functions.httpsCallable('getTokenCooldownStatus');
				const { data } = await getTokenCooldownStatusFn({ teamId: this.team.id });
				if (!data?.success) return;
				this.lastFetchedCooldownTeamId = this.team.id;
				if (data.resetAt != null) {
					this.updateTokenCooldownData({
						cooldownEndsAt: new Date(data.resetAt),
						cooldownData: {
							tokensRemaining: data.tokensRemaining,
							threshold: data.threshold,
							resetAt: data.resetAt,
							timeBucket: data.timeBucket,
							tokensSpent: data.tokensSpent,
						},
					});
				} else {
					this.updateTokenCooldownData({
						cooldownEndsAt: null,
						cooldownData: null,
					});
				}
			} catch (e) {
				console.error('fetchTokenCooldownStatus', e);
			} finally {
				this.fetchingTokenCooldownStatus = false;
			}
		},
		async onGenerateClick () {
			if (this.runnitButtonDisabled.disabled) return;
			if (this.activeDomainClaimRestriction) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: getDomainClaimRestrictionMessage(this.activeDomainClaimRestriction, this.displayLabels.team),
					show: true,
				});
				return;
			}
			if (getProxyAdminToken() && !confirm(`You are proxying another user. Are you sure you want to use their token balance to perform this action?`)) {
				return;
			}
			const UIFormValid = (!this.validate || this.validate()) // This is called to get the form validation errors up, but it doesn't block the run because it doesn't handle hidden fields properly
			const areInputsValid = this.areInputsValid();

			if (!areInputsValid.valid) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Form invalid, check fields: ${areInputsValid.reasons.join(', ')}`,
					show: true,
				});
				return;
			}

			if (
				!this.draftRunnitNodeRun // This means they haven't touched the form yet, or something...
			) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			this.$emit('onClick');
			const onError: Function = (e) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e.message ? `Error executing this Tool: ${e.message}` : 'Error executing this Tool, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};
			try {
				let runRunnitNodeData = {};
				if (this.selectedNode.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
					if (this.modelsState.modelDraft) {
						this.modelsState.modelDraft.createdAt = new Date();
						const modelAddedRef = await db.collection(`models`)
							.add({
								...this.modelsState.modelDraft,
								nameLowerCase: _trim(this.modelsState.modelDraft.name).toLowerCase(),
							});
						runRunnitNodeData = {
							...runRunnitNodeData,
							runnitSenderData: {
								modelId: modelAddedRef.id,
							},
						};
					} else {
						// There should already be a model at this point
						this.updateSnackbar({
							status: SNACKBAR_STATUS.ERROR,
							message: 'Please create a model first',
							show: true,
						});
						return;
					}
				}

				await this.executeRunnit(runRunnitNodeData);
				this.isModelTrainingDialogOpen = true;
			} catch (e) {
				onError(e);
			} finally {
				this.runnitState.isQueuingDraftRunnitNodeRun = false;
			}
		},
		async executeRunnit (runRunnitNodeData: any) {
			const onError: Function = (e) => {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: e.message ? `Error executing this Tool: ${e.message}` : 'Error executing this Tool, please reach out to report issues by going to Resources -> Help from the menu options',
					show: true,
				});
			};

			const staticInputValues: Record<RunnitNodeStaticFieldsKey, any> = {};
			_keys(this.determinedStaticFields).forEach((key: RunnitNodeStaticFieldsKey) => {
				// Only send values that are absolutely defined
				staticInputValues[key] = this.staticInputValues[key];
			});

			this.runnitState.isQueuingDraftRunnitNodeRun = true;
			const functionRef = functions
				.httpsCallable('runRunnitNode');

			const responseData = (await functionRef({
				...(window.localStorage.getItem('RUNNIT_USE_DEV_SERVER') && {
					useDevServer: true,
				}),
				nodeRunId: this.draftRunnitNodeRun.id,
				inputValues: this.inputValues,
				staticInputValues,
				teamId: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this._get(this.team, 'id') || null,
				...runRunnitNodeData,
			})).data;

			const { success, message } = responseData;

			if (!success) {
				console.error(`runRunnitNode returned a status of: ${success}`);

				// Check for TOKEN_COOLDOWN_ACTIVE error
				if (message === 'TOKEN_COOLDOWN_ACTIVE' || responseData.cooldownActive) {
					const tokensRemaining = responseData.tokensRemaining || 0;
					const threshold = responseData.threshold || 0;
					const timeBucket = responseData.timeBucket || '';
					const cooldownEndsAt = responseData.resetAt ? new Date(responseData.resetAt) : null;

					this.updateTokenCooldownData({
						cooldownEndsAt,
						cooldownData: {
							tokensSpent: responseData.tokensSpent || 0,
							tokensRemaining,
							threshold,
							resetAt: responseData.resetAt || '',
							timeBucket,
						},
						wasBlockedByCooldown: true,
					});

					this.$emit('open-cooldown-menu');
				} else if (responseData.teamInactive) {
					// The team was deactivated after the button last looked: say so as it is. An account
					// state, like a cooldown, not a failed generation, so it isn't tracked as one.
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: message || this.teamDeactivatedMessage,
						show: true,
					});
				} else {
					// Frustration signal: backend explicitly rejected the run with a
					// non-cooldown failure. Excludes cooldown blocks (which are a
					// rate-limit, not a real failure) so this metric stays clean.
					analytics.track(EVENTS.GENERATION_FAILED, {
						node_def_id: this._get(this.selectedNode, 'nodeDef.id', null),
						tool_title: this._get(this.selectedNode, 'nodeDef.title', null),
						error_message: typeof message === 'string' ? message.slice(0, 200) : null,
						runnit_id: this._get(this.runnitState, 'runnit.id', null),
					});
					onError(new Error(message));
				}
			} else {
				// Update cooldown data from success response (but don't set isCooldownActive to true - backend will catch it if needed)
				// Clear wasBlockedByCooldown on successful generation
				if (responseData.tokenCooldownData) {
					this.updateTokenCooldownData({
						cooldownEndsAt: responseData.tokenCooldownData.resetAt ? new Date(responseData.tokenCooldownData.resetAt) : null,
						cooldownData: responseData.tokenCooldownData,
						wasBlockedByCooldown: false,
					});
				} else {
					this.updateTokenCooldownData({
						cooldownEndsAt: null,
						cooldownData: null,
						wasBlockedByCooldown: false,
					});
				}

				// Core activation milestone — fired only on a successful generation
				// (the runRunnitNode call returned success), not on button click.
				// is_first_run is a per-user, per-device heuristic via localStorage:
				// it can't see generations from other devices/browsers, so treat it as
				// "first run on this device" — PostHog first-time funnels remain the
				// source of truth for true lifetime-first activation.
				const hasGeneratedKey: string = `rd_has_generated_${this._get(this.user, 'id', 'anon')}`;
				let isFirstRun: boolean = false;
				try {
					isFirstRun = !window.localStorage.getItem(hasGeneratedKey);
				} catch (e) { /* localStorage unavailable */ }

				analytics.track(EVENTS.BOARD_GENERATION_RUN, {
					node_def_id: this._get(this.selectedNode, 'nodeDef.id', null),
					tool_title: this._get(this.selectedNode, 'nodeDef.title', null),
					app_type: this._get(this.selectedNode, 'nodeDef.appType', null),
					cost_per_result: this._get(this.selectedNode, 'nodeDef.costPerResult', null),
					runnit_id: this._get(this.runnitState, 'runnit.id', null),
					is_team_run: this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM,
					is_first_run: isFirstRun,
				});

				// Distinct named event for the model-training subcase so the
				// training funnel can target it directly (vs filtering board_generation_run).
				if (this._get(this.selectedNode, 'nodeDef.appType', null) === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
					analytics.track(EVENTS.MODEL_TRAINING_STARTED, {
						node_def_id: this._get(this.selectedNode, 'nodeDef.id', null),
						tool_title: this._get(this.selectedNode, 'nodeDef.title', null),
						runnit_id: this._get(this.runnitState, 'runnit.id', null),
					});
				}

				if (isFirstRun) {
					try { window.localStorage.setItem(hasGeneratedKey, '1'); } catch (e) { /* localStorage unavailable */ }
				}

				if (this.$vuetify.breakpoint.xsOnly) {
					trackGtmRunGeneration(this.user, this.selectedNode.nodeDef);
					this.setSettingsDrawerOpen(false);
				}
			}
		},
		handleKeyDown (event) {
			// Only process if we're specifically looking for Cmd+Enter / Ctrl+Enter
			if (!this.canModifierEnterGenerate) return;

			// Only process Cmd/Ctrl+Enter, ignore all other keys
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				// Cmd/Ctrl + Enter to trigger the runnit
				event.preventDefault();
				this.onGenerateClick();
			}
		},
		imageAspectRatioError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			if (!v) return false; // no value so can't check its ratio
			const imageDetails: {
				width: number,
				height: number,
			} = v.nodeRunResult ? {
				width: v.nodeRunResult.file.width,
				height: v.nodeRunResult.file.height,
			} : v.upload ? {
				width: v.upload.width,
				height: v.upload.height,
			} : null;
			if (!imageDetails) return false; // we don't have any information on the image yet

			let { maxAspectRatio } = field.display as RunnitNodeFieldDisplayIMG || {};
			maxAspectRatio = maxAspectRatio || 3;

			const aspectRatio: BigNumber = new BigNumber(imageDetails.width).div(imageDetails.height);

			if (aspectRatio.isGreaterThan(maxAspectRatio)) {
				return `Image aspect ratio (${aspectRatio.toFixed(2)}:1) is too wide. Maximum allowed is ${maxAspectRatio}:1`;
			}
			if (aspectRatio.isLessThan(new BigNumber(maxAspectRatio).pow(-1))) {
				return `Image aspect ratio (1:${aspectRatio.pow(-1).toFixed(2)}) is too tall. Maximum allowed is 1:${maxAspectRatio}`;
			}

			return false;
		},
		imageMinDimensionError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			if (!v) return false; // no value so can't check its dimensions
			const imageDetails: {
				width: number,
				height: number,
			} = v.nodeRunResult ? {
				width: v.nodeRunResult.file.width,
				height: v.nodeRunResult.file.height,
			} : v.upload ? {
				width: v.upload.width,
				height: v.upload.height,
			} : null;
			if (!imageDetails) return false; // we don't have any information on the image yet

			const { minDimension } = field.display as RunnitNodeFieldDisplayIMG || {};
			if (!minDimension) return false; // no minimum dimension requirement

			if (imageDetails.width < minDimension) {
				return `Image width (${imageDetails.width}px) is too small. Minimum required is ${minDimension}px`;
			}
			if (imageDetails.height < minDimension) {
				return `Image height (${imageDetails.height}px) is too small. Minimum required is ${minDimension}px`;
			}

			return false;
		},
		inpaintingError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			if ((field.display as RunnitNodeFieldDisplayIMG)?.inpaintingRequired) {
				return v?.inpainting?.mask ? false : 'Mask is required';
			}
			return false
		},
		videoAspectRatioError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			// Handle both direct values and wrapped values
			let videoValue: RunnitNodeRunInputValue | null = null;
			if (v && typeof v === 'object') {
				if (v.video) {
					videoValue = v.video;
				} else if (v.nodeRunResult || v.upload) {
					videoValue = v;
				}
			}
			if (!videoValue) return false; // no value so can't check its ratio
			const videoDetails: {
				width: number,
				height: number,
			} = videoValue.nodeRunResult ? {
				width: videoValue.nodeRunResult.file.width,
				height: videoValue.nodeRunResult.file.height,
			} : videoValue.upload ? {
				width: videoValue.upload.width,
				height: videoValue.upload.height,
			} : null;
			if (!videoDetails) return false; // we don't have any information on the video yet

			let { maxAspectRatio } = field.display as RunnitNodeFieldDisplayVIDEO || {};
			maxAspectRatio = maxAspectRatio || 3;

			const aspectRatio: BigNumber = new BigNumber(videoDetails.width).div(videoDetails.height);

			if (aspectRatio.isGreaterThan(maxAspectRatio)) {
				return `Video aspect ratio (${aspectRatio.toFixed(2)}:1) is too wide. Maximum allowed is ${maxAspectRatio}:1`;
			}
			if (aspectRatio.isLessThan(new BigNumber(maxAspectRatio).pow(-1))) {
				return `Video aspect ratio (1:${aspectRatio.pow(-1).toFixed(2)}) is too tall. Maximum allowed is 1:${maxAspectRatio}`;
			}

			return false;
		},
		videoMinDimensionError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			// Handle both direct values and wrapped values
			let videoValue: RunnitNodeRunInputValue | null = null;
			if (v && typeof v === 'object') {
				if (v.video) {
					videoValue = v.video;
				} else if (v.nodeRunResult || v.upload) {
					videoValue = v;
				}
			}
			if (!videoValue) return false; // no value so can't check its dimensions
			const videoDetails: {
				width: number,
				height: number,
			} = videoValue.nodeRunResult ? {
				width: videoValue.nodeRunResult.file.width,
				height: videoValue.nodeRunResult.file.height,
			} : videoValue.upload ? {
				width: videoValue.upload.width,
				height: videoValue.upload.height,
			} : null;
			if (!videoDetails) return false; // we don't have any information on the video yet

			const { minDimension } = field.display as RunnitNodeFieldDisplayVIDEO || {};
			if (!minDimension) return false; // no minimum dimension requirement

			if (videoDetails.width < minDimension) {
				return `Video width (${videoDetails.width}px) is too small. Minimum required is ${minDimension}px`;
			}
			if (videoDetails.height < minDimension) {
				return `Video height (${videoDetails.height}px) is too small. Minimum required is ${minDimension}px`;
			}

			return false;
		},
		videoMaxWidthError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			// Handle both direct values and wrapped values
			let videoValue: RunnitNodeRunInputValue | null = null;
			if (v && typeof v === 'object') {
				if (v.video) {
					videoValue = v.video;
				} else if (v.nodeRunResult || v.upload) {
					videoValue = v;
				}
			}
			if (!videoValue) return false; // no value so can't check its width
			const videoDetails: {
				width: number,
			} = videoValue.nodeRunResult ? {
				width: videoValue.nodeRunResult.file.width,
			} : videoValue.upload ? {
				width: videoValue.upload.width,
			} : null;
			if (!videoDetails || !videoDetails.width) return false; // we don't have any information on the video yet

			const { maxWidth } = field.display as RunnitNodeFieldDisplayVIDEO || {};
			if (!maxWidth) return false; // no maximum width requirement

			if (videoDetails.width > maxWidth) {
				return `Video width (${videoDetails.width}px) exceeds maximum allowed width of ${maxWidth}px`;
			}

			return false;
		},
		videoMaxHeightError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			// Handle both direct values and wrapped values
			let videoValue: RunnitNodeRunInputValue | null = null;
			if (v && typeof v === 'object') {
				if (v.video) {
					videoValue = v.video;
				} else if (v.nodeRunResult || v.upload) {
					videoValue = v;
				}
			}
			if (!videoValue) return false; // no value so can't check its height
			const videoDetails: {
				height: number,
			} = videoValue.nodeRunResult ? {
				height: videoValue.nodeRunResult.file.height,
			} : videoValue.upload ? {
				height: videoValue.upload.height,
			} : null;
			if (!videoDetails || !videoDetails.height) return false; // we don't have any information on the video yet

			const { maxHeight } = field.display as RunnitNodeFieldDisplayVIDEO || {};
			if (!maxHeight) return false; // no maximum height requirement

			if (videoDetails.height > maxHeight) {
				return `Video height (${videoDetails.height}px) exceeds maximum allowed height of ${maxHeight}px`;
			}

			return false;
		},
		videoMaxDurationError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			// Handle both direct values and wrapped values
			let videoValue: RunnitNodeRunInputValue | null = null;
			if (v && typeof v === 'object') {
				if (v.video) {
					videoValue = v.video;
				} else if (v.nodeRunResult || v.upload) {
					videoValue = v;
				}
			}
			if (!videoValue) return false; // no value so can't check its duration
			const videoDetails: {
				duration: number,
			} = videoValue.nodeRunResult ? {
				duration: videoValue.nodeRunResult.file.duration,
			} : videoValue.upload ? {
				duration: videoValue.upload.duration,
			} : null;
			if (!videoDetails || !videoDetails.duration) return false; // we don't have any information on the video yet

			const { maxDuration } = field.display as RunnitNodeFieldDisplayVIDEO || {};
			if (!maxDuration) return false; // no maximum duration requirement

			if (videoDetails.duration > maxDuration) {
				return `Video duration (${videoDetails.duration}s) exceeds maximum allowed duration of ${maxDuration}s`;
			}

			return false;
		},
		maxImagesError (v: RunnitNodeRunInputValue, field: RunnitNodeField) {
			const maxImages = (field.display as RunnitNodeFieldDisplayIMGS)?.maxImages;
			if (!maxImages) return false;
			const numImages: number = (v?.uploads?.length || 0) + (v?.nodeRunResults?.length || 0);
			return numImages <= maxImages ? false : `Maximum of ${maxImages} images allowed`;
		},
		promptError (v: RunnitNodeRunInputValue | string, field: RunnitNodeField) {
			const maxCharacters = (field.display as RunnitNodeFieldDisplayPROMPT)?.maxLength || DEFAULT_TEXT_MAX_LENGTH;
			if (!maxCharacters) return false;
			const length = _isString(v) ? v.length : (v.value || '').length;
			if (length > maxCharacters) {
				return `Maximum of ${maxCharacters} characters allowed`;
			}
			return false;
		},
		upscaleFactorError (v: RunnitNodeRunInputValue, field: RunnitNodeField, inputs: Record<FieldDefUuid, RunnitNodeRunInputValue>) {
			// Handle both direct values and wrapped values
			let upscaleFactor;
			if (typeof v === 'number') {
				upscaleFactor = v;
			} else if (v && typeof v === 'object' && v.value !== undefined) {
				upscaleFactor = v.value;
			} else {
				return false; // No upscale factor value to validate
			}

			const maxWidth = (field.display as RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER)?.maxWidth || 3840;
			const maxHeight = (field.display as RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER)?.maxHeight || 2160;

			// Get the input dimensions from the dataFieldDefUuid
			const dataFieldDefUuid = (field.display as RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER)?.dataFieldDefUuid;
			if (!dataFieldDefUuid) {
				return false; // No source field to check dimensions
			}

			const dataInput = inputs[dataFieldDefUuid];
			if (!dataInput) {
				return false; // No input data to check
			}

			// One reader for this shape, shared with the Non-Upscaling Target rule.
			const inputDimensions = extractInputMediaDimensions(dataInput);
			if (!inputDimensions) {
				return false; // No dimensions to validate
			}
			const { width: inputWidth, height: inputHeight } = inputDimensions;

			// Calculate final dimensions
			const finalWidth = Math.round(inputWidth * upscaleFactor);
			const finalHeight = Math.round(inputHeight * upscaleFactor);

			// Check if either dimension exceeds the maximum
			if (finalWidth > maxWidth) {
				return `Final width (${finalWidth}px) exceeds maximum allowed width of ${maxWidth}px`;
			}
			if (finalHeight > maxHeight) {
				return `Final height (${finalHeight}px) exceeds maximum allowed height of ${maxHeight}px`;
			}

			return false;
		},
		// Validates ONE leaf field. Group recursion used to live here, which was the
		// bug: areInputsValid checked isFieldVisibleAndEnabled only on top-level
		// entries and then this method walked into group children without
		// re-checking, so a hidden field nested inside a visible group was still
		// validated. areInputsValid now iterates eachVisibleLeafField, which checks
		// visibility at every level. See PR #729.
		validateField (field: RunnitNodeField, inputs: Record<FieldDefUuid, RunnitNodeRunInputValue>): { valid: boolean, reasons: string[] } {
			let valid = true;
			const reasons: string[] = [];
			const input = inputs[field.fieldDefUuid];
			if (isFieldRequired(field, inputs)) {
				if (!input) {
					valid = false;
					reasons.push(`Missing required field: ${field.label}`);
				} else if (field.type === RUNNIT_NODE_FIELD_TYPE.PROMPT) {
					const value = _isObject(input) ? (input as RunnitNodeRunInputValue).value : input;
					const cleanValue = value?.trim();
					if (!cleanValue) {
						valid = false;
						reasons.push(`Missing required field: ${field.label}`);
					}
				}
			}

			if (field.type === RUNNIT_NODE_FIELD_TYPE.IMG) {
				const aspectRatioError = this.imageAspectRatioError(input, field);
				if (aspectRatioError) {
					valid = false;
					reasons.push(aspectRatioError);
				}
				const minDimensionError = this.imageMinDimensionError(input, field);
				if (minDimensionError) {
					valid = false;
					reasons.push(minDimensionError);
				}
				if ((field.display as RunnitNodeFieldDisplayIMG)?.hasInpainting) {
					const inpaintingError = this.inpaintingError(input, field);
					if (inpaintingError) {
						valid = false;
						reasons.push(inpaintingError);
					}
				}
			}
			if (field.type === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				const maxImagesError = this.maxImagesError(input, field);
				if (maxImagesError) {
					valid = false;
					reasons.push(maxImagesError);
				}
			}

			if (field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO) {
				const aspectRatioError = this.videoAspectRatioError(input, field);
				if (aspectRatioError) {
					valid = false;
					reasons.push(aspectRatioError);
				}
				const minDimensionError = this.videoMinDimensionError(input, field);
				if (minDimensionError) {
					valid = false;
					reasons.push(minDimensionError);
				}
				const maxWidthError = this.videoMaxWidthError(input, field);
				if (maxWidthError) {
					valid = false;
					reasons.push(maxWidthError);
				}
				const maxHeightError = this.videoMaxHeightError(input, field);
				if (maxHeightError) {
					valid = false;
					reasons.push(maxHeightError);
				}
				const maxDurationError = this.videoMaxDurationError(input, field);
				if (maxDurationError) {
					valid = false;
					reasons.push(maxDurationError);
				}
			}

			if (field.type === RUNNIT_NODE_FIELD_TYPE.PROMPT) {
				const promptError = this.promptError(input, field);
				if (promptError) {
					valid = false;
					reasons.push(promptError);
				}
			}

			if (field.type === RUNNIT_NODE_FIELD_TYPE.UPSCALE_FACTOR_SLIDER) {
				// Check dimension constraints
				const upscaleFactorError = this.upscaleFactorError(input, field, inputs);
				if (upscaleFactorError) {
					valid = false;
					reasons.push(upscaleFactorError);
				}
				// Check value/option/step validity
				const display = field.display as RunnitNodeFieldDisplayUPSCALE_FACTOR_SLIDER;
				const validationResult = validateSliderValue(display, input, { requireStepAlignment: true });
				if (!validationResult.valid) {
					valid = false;
					reasons.push(
						`${validationResult.reason || 'Invalid value'} for ${field.label || field.fieldDefUuid}`
					);
				}
			}

			// Validate WIDTH_HEIGHT field options
			if (field.type === RUNNIT_NODE_FIELD_TYPE.WIDTH_HEIGHT && input) {
				const display = field.display as RunnitNodeFieldDisplayWIDTH_HEIGHT;
				const validationResult = validateWidthHeightValue(display, input);
				if (!validationResult.valid) {
					valid = false;
					reasons.push(
						`${validationResult.reason || 'Invalid resolution option'} for ${field.label || field.fieldDefUuid}`
					);
				}
			}

			// Block a Non-Upscaling Target (enforceTargetLargerThanInput). An ordinary
			// per-field check now that areInputsValid walks every visible leaf: no
			// separate pass, and the same rule interpretation both servers use.
			//
			// An affordance, not the gate: runRunnitNode and runGenerateApi.createRun
			// reject before the token debit. See
			// docs/adr/0033-non-upscaling-targets-rejected-before-token-debit.md.
			if (field.type === RUNNIT_NODE_FIELD_TYPE.SINGLE_SELECT) {
				const { targetLargerThanInputError } = getLogicOnFieldsFromField(field, inputs);
				if (targetLargerThanInputError) {
					valid = false;
					reasons.push(targetLargerThanInputError);
				}
			}

			// Validate SINGLE_SELECT field options
			if (field.type === RUNNIT_NODE_FIELD_TYPE.SINGLE_SELECT && input) {
				const display = field.display as RunnitNodeFieldDisplaySINGLE_SELECT;
				const validationResult = validateSingleSelectValue(display, input);
				if (!validationResult.valid) {
					valid = false;
					reasons.push(
						`${validationResult.reason || 'Invalid option'} for ${field.label || field.fieldDefUuid}`
					);
				}
			}

			// Validate SLIDER/CFG/STEPS field range and step
			if (
				(field.type === RUNNIT_NODE_FIELD_TYPE.SLIDER ||
					field.type === RUNNIT_NODE_FIELD_TYPE.CFG ||
					field.type === RUNNIT_NODE_FIELD_TYPE.STEPS) &&
				input
			) {
				const display = field.display as RunnitNodeFieldDisplaySLIDER | RunnitNodeFieldDisplayCFG | RunnitNodeFieldDisplaySTEPS;
				// For run-time validation, always check step alignment (step defaults to 1 if missing)
				const validationResult = validateSliderValue(display, input, { requireStepAlignment: true });
				if (!validationResult.valid) {
					valid = false;
					reasons.push(
						`${validationResult.reason || 'Invalid value'} for ${field.label || field.fieldDefUuid}`
					);
				}
			}

			return { valid, reasons };
		},
		areInputsValid (): { valid: boolean, reasons: string[] } {
			if (!this.inputValues) return { valid: false, reasons: ['missing input data'] };
			let valid = true;
			const reasons: string[] = [];
			// One walk, visibility checked at every level including inside groups.
			eachVisibleLeafField(this.selectedNode.nodeDef.fields, this.inputValues)
				.forEach((field: RunnitNodeField) => {
					const fieldResults = this.validateField(field, this.inputValues);
					if (!fieldResults.valid) {
						valid = false;
						reasons.push(...fieldResults.reasons);
					}
				});
			eachUnsatisfiedRequiredGroup(this.selectedNode.nodeDef.fields, this.inputValues)
				.forEach((group: RunnitNodeFieldGroup) => {
					valid = false;
					reasons.push(`Select at least one: ${group.label}`);
				});

			// TRAINER-specific validation: model draft metadata (Title, Description, Access Level)
			if (this.selectedNode?.nodeDef?.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER) {
				const title = _trim(this.modelsState?.modelDraft?.name || '');
				const accessLevel = this.modelsState?.modelDraft?.accessLevel;

				if (!title) {
					valid = false;
					reasons.push('Missing required field: Title');
				}

				if (!accessLevel) {
					valid = false;
					reasons.push('Missing required field: Access Level');
				}
			}

			return { valid, reasons };
		},
	},
	components: {
		RunnitModelTrainingDialog,
		TokensSVG,
		BaseButton,
		CooldownLabelButton,
	},
});
